// Panel del módulo Mantenimiento — KPIs (GET /kpis) + donut de OTs por estado
// + barras de activos por planta. Mismos tokens de tema que C1/D2 (CSS vars
// var(--color-*) en Recharts para que light/dark funcione sin JS).

import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { KpiCard } from '../../components/ui/kpi-card';
import { MantenimientoShell, CargandoModulo, ErrorModulo } from './MantenimientoShell';
import { useMantenimientoKpis } from './queries';
import { ESTADOS_OT, ESTADO_OT_CHART_COLOR, ESTADO_OT_LABEL, fmtNum } from './shared';

const TOOLTIP_STYLE = {
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--color-fg)',
} as const;

export function MantenimientoDashboardPage() {
  const { data, isLoading, isError, error } = useMantenimientoKpis();

  return (
    <MantenimientoShell
      titulo="Mantenimiento"
      subtitulo="Panel del módulo · órdenes de trabajo, activos y repuestos"
    >
      {isLoading ? <CargandoModulo mensaje="Cargando panel de mantenimiento…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}
      {data ? (
        <div className="flex flex-col gap-4">
          <Card>
            <CardContent className="grid grid-cols-1 gap-4 p-5 pt-5 sm:grid-cols-2 lg:grid-cols-5">
              <KpiCard
                label="OT abiertas"
                value={fmtNum(data.ot_por_estado.abierta)}
                sub="Sin programar ni iniciar"
              />
              <KpiCard
                label="OT en proceso"
                value={fmtNum(data.ot_por_estado.en_proceso)}
                sub={`${fmtNum(data.ot_por_estado.programada)} programada(s)`}
                divider
              />
              <KpiCard
                label="OT vencidas"
                value={fmtNum(data.ot_vencidas)}
                sub={data.ot_vencidas > 0 ? 'Fecha programada superada' : 'Al día'}
                subTone={data.ot_vencidas > 0 ? 'crit' : 'ok'}
                divider
              />
              <KpiCard
                label="Repuestos bajo mínimo"
                value={fmtNum(data.repuestos_bajo_minimo)}
                sub={
                  data.repuestos_bajo_minimo > 0 ? (
                    <Link to="/mantenimiento/repuestos" className="underline underline-offset-2">
                      Revisar inventario
                    </Link>
                  ) : (
                    'Inventario sano'
                  )
                }
                subTone={data.repuestos_bajo_minimo > 0 ? 'warn' : 'ok'}
                divider
              />
              <KpiCard
                label="Activos"
                value={fmtNum(data.activos_total)}
                sub={`${data.activos_por_planta.length} planta(s)`}
                divider
              />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Órdenes por estado</CardTitle>
              </CardHeader>
              <CardContent>
                <DonutOtsPorEstado data={data.ot_por_estado} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Activos por planta</CardTitle>
              </CardHeader>
              <CardContent>
                {data.activos_por_planta.length === 0 ? (
                  <p className="py-10 text-center text-sm text-fg-tertiary">
                    Aún no hay activos registrados.{' '}
                    <Link to="/mantenimiento/activos" className="underline underline-offset-2">
                      Cree el primero
                    </Link>
                    .
                  </p>
                ) : (
                  <div className="h-[240px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data.activos_por_planta}
                        margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="var(--color-border)"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="planta_nombre"
                          tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
                          axisLine={{ stroke: 'var(--color-border)' }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          cursor={{ fill: 'var(--color-surface-overlay)' }}
                          formatter={(value) => [fmtNum(value as number), 'Activos']}
                        />
                        <Bar
                          dataKey="activos"
                          fill="var(--color-accent)"
                          radius={[3, 3, 0, 0]}
                          maxBarSize={48}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}
    </MantenimientoShell>
  );
}

function DonutOtsPorEstado({ data }: { data: Record<(typeof ESTADOS_OT)[number], number> }) {
  const total = ESTADOS_OT.reduce((sum, e) => sum + data[e], 0);
  const serie = ESTADOS_OT.map((estado) => ({
    estado,
    label: ESTADO_OT_LABEL[estado],
    valor: data[estado],
    color: ESTADO_OT_CHART_COLOR[estado],
  })).filter((d) => d.valor > 0);

  if (total === 0) {
    return (
      <p className="py-10 text-center text-sm text-fg-tertiary">
        Aún no hay órdenes de trabajo registradas.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="relative">
        <PieChart width={220} height={220}>
          <Pie
            data={serie}
            dataKey="valor"
            nameKey="label"
            innerRadius={62}
            outerRadius={92}
            paddingAngle={2}
            strokeWidth={2}
            stroke="var(--color-surface-elevated)"
          >
            {serie.map((d) => (
              <Cell key={d.estado} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, name) => [fmtNum(value as number), String(name)]}
          />
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-medium tabular-nums text-fg">{fmtNum(total)}</span>
          <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">OTs</span>
        </div>
      </div>
      <ul className="flex min-w-40 flex-1 flex-col gap-1.5">
        {serie.map((d) => (
          <li key={d.estado} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden
              className="inline-block size-2 flex-shrink-0 rounded-full"
              style={{ background: d.color }}
            />
            <span className="flex-1 text-fg-secondary">{d.label}</span>
            <span className="tabular-nums text-fg">{fmtNum(d.valor)}</span>
            <span className="w-10 text-right text-xs tabular-nums text-fg-tertiary">
              {Math.round((d.valor / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
